import { useState, useRef, useEffect, useCallback } from 'react';
import { useAuth } from '../lib/auth';

const API = process.env.REACT_APP_API_URL || 'https://web-production-120c.up.railway.app';

const QUICK = [
  'I received a suspicious email',
  'Run a digital exposure check',
  'I need travel security advice',
  'Help me secure my devices',
  'Run a dark web check',
  'I need emergency assistance',
];

async function callShaheen(clientId, message) {
  try {
    const token = localStorage.getItem('shaheen_token');
    const res = await fetch(`${API}/chat`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ client_id: clientId, message }),
    });
    if (!res.ok) {
      const errText = await res.text().catch(() => 'no body');
      return `[Portal error ${res.status}] ${errText.slice(0, 200)}`;
    }
    const data = await res.json();
    return data.response || 'I am experiencing a brief issue. Please try again in a moment.';
  } catch (e) {
    return `[Network error] ${e.message}`;
  }
}

function fmt(d) {
  if (!d) return '';
  try { return new Date(d).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }); }
  catch { return ''; }
}

function fmtDate(d) {
  if (!d) return '';
  try {
    const date = new Date(d);
    const today = new Date();
    const diff = Math.floor((today - date) / 86400000);
    if (diff === 0) return 'Today';
    if (diff === 1) return 'Yesterday';
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  } catch { return ''; }
}

export default function Chat() {
  const { client } = useAuth();
  const initials = client?.name?.split(' ').map(w => w[0]).join('').slice(0,2).toUpperCase() || 'VIP';
  const greeting = `Good ${new Date().getHours() < 12 ? 'morning' : new Date().getHours() < 18 ? 'afternoon' : 'evening'}, ${client?.name?.split(' ')[0] || 'there'}. Your protection is active. How can I assist you today?`;

  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [allHistory, setAllHistory] = useState([]);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  const loadHistory = useCallback(async () => {
    const clientId = client?.id;
    if (!clientId) {
      setMsgs([{ role: 'assistant', text: greeting, time: new Date() }]);
      setHistoryLoaded(true);
      return;
    }
    try {
      const token = localStorage.getItem('shaheen_token');
      const r = await fetch(`${API}/chat/history/${clientId}`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      });
      if (r.status === 401) {
        // Session expired — clear stale token, show re-login message
        localStorage.removeItem('shaheen_token');
        localStorage.removeItem('shaheen_client');
        window.location.reload();
        return;
      }
      const data = await r.json();
      const history = (data.messages || [])
        .filter(m => m.content && m.content.trim() !== '')
        .map(m => ({ role: m.role, text: m.content, time: m.timestamp ? new Date(m.timestamp.endsWith('Z') ? m.timestamp : m.timestamp + 'Z') : new Date() }));
      setAllHistory(history);

      // Surface any assistant messages (advisor replies, scan results, alerts) that
      // arrived since the client last had the chat open.
      const lastSeenKey = `shaheen_last_seen_${clientId}`;
      const lastSeen = localStorage.getItem(lastSeenKey);
      const lastSeenDate = lastSeen ? new Date(lastSeen) : null;
      localStorage.setItem(lastSeenKey, new Date().toISOString());
      const unread = lastSeenDate
        ? history.filter(m => m.role === 'assistant' && m.time > lastSeenDate)
        : [];

      const initial = [{ role: 'assistant', text: greeting, time: new Date() }];
      setMsgs(unread.length > 0 ? [...initial, ...unread] : initial);
    } catch {
      setMsgs([{ role: 'assistant', text: greeting, time: new Date() }]);
    } finally {
      setHistoryLoaded(true);
    }
  }, [client?.id]); // eslint-disable-line

  useEffect(() => { loadHistory(); }, []); // eslint-disable-line

  // Check for prefill from services page
  useEffect(() => {
    const prefill = localStorage.getItem('shaheen_prefill');
    if (prefill && historyLoaded) {
      localStorage.removeItem('shaheen_prefill');
      send(prefill);
    }
  }, [historyLoaded]); // eslint-disable-line

  useEffect(() => {
    if (historyLoaded) bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [msgs, historyLoaded]);

  const send = async (text) => {
    const msg = (text || input).trim();
    if (!msg || loading) return;
    setInput('');
    setShowHistory(false);
    const now = new Date();
    const userEntry = { role: 'user', text: msg, time: now };
    setMsgs(m => [...m, userEntry]);
    setLoading(true);
    const reply = await callShaheen(client?.id || 'guest', msg);
    const assistantEntry = { role: 'assistant', text: reply, time: new Date() };
    setMsgs(m => [...m, assistantEntry]);
    setAllHistory(h => [...h, userEntry, assistantEntry]);
    setLoading(false);
  };

  // Group messages by date for history view
  const groupByDate = (messages) => {
    const groups = {};
    messages.forEach(m => {
      const key = fmtDate(m.time);
      if (!groups[key]) groups[key] = [];
      groups[key].push(m);
    });
    return Object.entries(groups);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', position: 'relative' }}>
      {/* Header */}
      <div style={{ padding: '12px 16px', borderBottom: '0.5px solid var(--border)', background: 'var(--bg-surface)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--green)' }} />
          <div>
            <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--gold)' }}>Shaheen Concierge</div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Always available</div>
          </div>
        </div>
        <button
          onClick={() => { setShowHistory(h => { const next = !h; if (next) loadHistory(); return next; }); }}
          style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: showHistory ? 'var(--gold)' : 'var(--text-muted)', background: showHistory ? 'var(--gold-dim)' : 'var(--bg-raised)', border: `0.5px solid ${showHistory ? 'var(--gold)' : 'var(--border)'}`, borderRadius: 8, padding: '6px 12px', cursor: 'pointer' }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
          History
        </button>
      </div>

      {/* History panel */}
      {showHistory && (
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px', background: 'var(--bg-base)' }}>
          {allHistory.length === 0 ? (
            <div style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: 13, padding: 40 }}>No conversation history yet</div>
          ) : (
            groupByDate(allHistory).map(([date, messages]) => (
              <div key={date}>
                <div style={{ fontSize: 10, color: 'var(--text-muted)', letterSpacing: '0.06em', textAlign: 'center', margin: '12px 0 8px', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ flex: 1, height: '0.5px', background: 'var(--border)' }} />
                  {date.toUpperCase()}
                  <div style={{ flex: 1, height: '0.5px', background: 'var(--border)' }} />
                </div>
                {messages.map((m, i) => {
                  const isUser = m.role === 'user';
                  return (
                    <div key={i} style={{ display: 'flex', gap: 8, maxWidth: '85%', alignSelf: isUser ? 'flex-end' : 'flex-start', flexDirection: isUser ? 'row-reverse' : 'row', marginBottom: 10, marginLeft: isUser ? 'auto' : 0 }}>
                      <div style={{ width: 24, height: 24, borderRadius: '50%', background: isUser ? 'var(--gold-dim)' : 'var(--bg-raised)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 500, color: 'var(--gold)', flexShrink: 0, marginTop: 2 }}>
                        {isUser ? initials : 'S'}
                      </div>
                      <div>
                        <div style={{ background: isUser ? 'var(--bg-raised)' : 'var(--bg-surface)', border: `0.5px solid ${isUser ? 'var(--border-hover)' : 'var(--border)'}`, borderRadius: 10, padding: '8px 12px', fontSize: 12, color: isUser ? 'var(--text-primary)' : 'var(--text-secondary)', lineHeight: 1.6, wordBreak: 'break-word' }}>
                          {m.text.split('\n').map((line, j, arr) => <span key={j}>{line}{j < arr.length - 1 && <br />}</span>)}
                        </div>
                        <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2, textAlign: isUser ? 'right' : 'left' }}>{fmt(m.time)}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ))
          )}
          <div style={{ textAlign: 'center', marginTop: 20 }}>
            <button onClick={() => setShowHistory(false)} style={{ fontSize: 12, color: 'var(--gold)', background: 'var(--gold-dim)', border: '0.5px solid var(--gold)', borderRadius: 8, padding: '8px 20px', cursor: 'pointer' }}>
              Back to chat
            </button>
          </div>
        </div>
      )}

      {/* Chat messages */}
      {!showHistory && (
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          {msgs.map((m, i) => {
            const isUser = m.role === 'user';
            return (
              <div key={i} style={{ display: 'flex', gap: 8, maxWidth: '85%', alignSelf: isUser ? 'flex-end' : 'flex-start', flexDirection: isUser ? 'row-reverse' : 'row' }}>
                <div style={{ width: 28, height: 28, borderRadius: '50%', background: isUser ? 'var(--gold-dim)' : 'var(--bg-raised)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 500, color: 'var(--gold)', flexShrink: 0, marginTop: 2 }}>
                  {isUser ? initials : 'S'}
                </div>
                <div>
                  <div style={{ background: isUser ? 'var(--bg-raised)' : 'var(--bg-surface)', border: `0.5px solid ${isUser ? 'var(--border-hover)' : 'var(--border)'}`, borderRadius: 12, padding: '9px 13px', fontSize: 13, color: isUser ? 'var(--text-primary)' : 'var(--text-secondary)', lineHeight: 1.65, wordBreak: 'break-word' }}>
                    {m.text.split('\n').map((line, j, arr) => <span key={j}>{line}{j < arr.length - 1 && <br />}</span>)}
                  </div>
                  <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 3, textAlign: isUser ? 'right' : 'left' }}>{fmt(m.time)}</div>
                </div>
              </div>
            );
          })}
          {loading && (
            <div style={{ display: 'flex', gap: 8, maxWidth: '85%' }}>
              <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'var(--bg-raised)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, color: 'var(--gold)', flexShrink: 0 }}>S</div>
              <div style={{ background: 'var(--bg-surface)', border: '0.5px solid var(--border)', borderRadius: 12, padding: '12px 16px', display: 'flex', gap: 5, alignItems: 'center' }}>
                {[0,1,2].map(i => <div key={i} style={{ width: 5, height: 5, borderRadius: '50%', background: 'var(--text-muted)', animation: `blink 1.2s ${i*0.2}s infinite` }} />)}
              </div>
            </div>
          )}
          <div ref={bottomRef} />
        </div>
      )}

      {/* Input area */}
      {!showHistory && (
        <div style={{ padding: '10px 14px', borderTop: '0.5px solid var(--border)', background: 'var(--bg-surface)', flexShrink: 0 }}>
          <div style={{ display: 'flex', gap: 6, marginBottom: 8, overflowX: 'auto', paddingBottom: 4, scrollbarWidth: 'none' }}>
            {QUICK.map(q => (
              <button key={q} onClick={() => send(q)} style={{ fontSize: 11, background: 'var(--bg-raised)', border: '0.5px solid var(--border)', color: 'var(--text-secondary)', padding: '5px 11px', borderRadius: 14, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}>
                {q}
              </button>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
            <textarea
              ref={inputRef}
              style={{ flex: 1, background: 'var(--bg-raised)', border: '0.5px solid var(--border)', borderRadius: 20, padding: '10px 16px', fontSize: 13, color: 'var(--text-primary)', outline: 'none', fontFamily: 'inherit', resize: 'none', minHeight: 42, maxHeight: 120, lineHeight: 1.5 }}
              value={input}
              onChange={e => setInput(e.target.value)}
              placeholder="Message Shaheen..."
              rows={1}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
            />
            <button onClick={() => send()} style={{ width: 40, height: 40, borderRadius: '50%', background: input.trim() ? 'var(--gold)' : 'var(--bg-raised)', display: 'flex', alignItems: 'center', justifyContent: 'center', border: 'none', flexShrink: 0, cursor: 'pointer', transition: 'background 0.15s' }}>
              <svg width="15" height="15" viewBox="0 0 16 16" fill={input.trim() ? '#08080f' : 'var(--text-muted)'}><path d="M2 8L14 2L10 8L14 14Z"/></svg>
            </button>
          </div>
        </div>
      )}

      <style>{`@keyframes blink { 0%,80%,100%{opacity:.3} 40%{opacity:1} }`}</style>
    </div>
  );
}
